import { useMemo } from "react";

import { Drawer, SidebarForMini, DistrictInfo, UserInfo } from "./UI";
import { useSidebarContext } from "./context/SidebarStateContext";
import SidebarMenuList from "./SidebarMenuList";
import { useMenuSelectionContext } from "./context/MenuSelectionContext";

/**
 * SidebarDistrict Component (District Sidebar)
 * --------------------------------------
 * Handles rendering of both the main "District" sidebar and its mini/collapsed version.
 * Responsibilities:
 *  - Display district info (logo, name, address)
 *  - Render district-specific menu items
 *  - Support mini/collapsed sidebar view with InviteCode section
 *  - Integrate with context and Redux for sidebar/menu state
 */
const SidebarDistrict = () => {

  // Sidebar context → manages open/close state of the district sidebar
  const { showSidebar } = useSidebarContext();

  // Menu selection context → handles toggling selected menu item in mini sidebar
  const { toggleSelectedIndex } = useMenuSelectionContext();

  /**
   * Memoized main menu content for district sidebar
   * - Prevents unnecessary re-renders
   * - Uses full menu (SidebarMenuList)
   */
  const menuContent = useMemo(() => <SidebarMenuList />, []);

  /**
   * Memoized mini menu content for collapsed sidebar
   * - filterBar=true enables simplified icon-only menu
   * - Useful for mini sidebar or mobile screens
   */
  const miniMenuContent = useMemo(
    () => <SidebarMenuList filterBar={true} />,
    []
  );

  return (
    <>
      {/* ===================== MAIN DISTRICT SIDEBAR ===================== */}
      <Drawer
        variant="permanent" // Always mounted, visibility handled via `open`
        open={showSidebar} // Controlled via context
        paddingTop={7} // Offset from top (if needed)
      >
        {/* Top section: District Info (logo, name, address) */}
        <DistrictInfo
          onAvatarClick={toggleSelectedIndex} // Toggle submenu/profile panel in mini sidebar
        />

        {/* Middle section: Menu content */}
        {menuContent}
        <UserInfo />
      </Drawer>

      {/* ===================== MINI SIDEBAR ===================== */}
      <SidebarForMini
        showInviteCode={true} // Show InviteCode component in mini sidebar
        isMain={false} // Indicates this is not the main app sidebar
        showUserInfo={true} // Show user info section
      >
        {/* Mini menu items (icon-only / simplified) */}
        {miniMenuContent}
      </SidebarForMini>
    </>
  );
};

export default SidebarDistrict;
